/**
 * pages/AcceptInvite.tsx
 *
 * Landing page for invited users arriving from their invitation email.
 * The email links here (instead of directly to the Supabase OTP URL) to prevent
 * email security scanners from consuming the one-time-use token before the user clicks.
 * The user clicks "Activer mon compte", which triggers a fresh OTP generation and redirects
 * to the Supabase verify endpoint.
 * Created: 2026-04-14
 * Updated Apr 2026: H1 "Bienvenue" uses Playfair Display (font-serif font-medium) per design system.
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import { AlertTriangle } from 'lucide-react';

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(
    decodeURIComponent(searchParams.get('email') ?? '')
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleActivate = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError('Veuillez saisir votre adresse email');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const { data, error: fnError } = await supabase.functions.invoke(
        'request-invite-link',
        { body: { email: trimmed } }
      );

      if (fnError || !data) {
        setError("Une erreur est survenue. Veuillez réessayer ou contacter votre administrateur.");
        return;
      }

      if (data.error) {
        setError(data.error);
        return;
      }

      if (!data.actionLink) {
        setError("Lien d'activation non reçu. Veuillez réessayer.");
        return;
      }

      window.location.href = data.actionLink;
    } catch {
      setError("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleActivate();
  };

  return (
    <div className="min-h-screen bg-neutral-100 flex items-center justify-center px-4">
      <Card className="w-full max-w-md shadow-soft-xl">
        <div className="text-center mb-8">
          <img
            src="/logo-qollect.png"
            alt="Qollect"
            className="h-10 w-auto mx-auto mb-6"
          />
          <h1 className="text-xl font-serif font-medium text-neutral-900 mb-2 tracking-tight">
            Bienvenue
          </h1>
          <p className="text-sm text-neutral-500 leading-relaxed">
            Vous avez reçu une invitation pour rejoindre la plateforme. Cliquez sur le bouton ci-dessous pour activer votre compte.
          </p>
        </div>

        <div className="space-y-4">
          <Input
            label="Adresse email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={handleKeyDown}
            autoComplete="email"
            placeholder="votre@email.com"
          />

          {error && (
            <div className="flex items-start gap-2.5 bg-error-50 border border-error-200 text-error-700 px-4 py-3 rounded-xl text-sm">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <Button
            type="button"
            fullWidth
            size="md"
            loading={loading}
            onClick={handleActivate}
          >
            Activer mon compte
          </Button>
        </div>

        <p className="mt-6 text-center text-xs text-neutral-400">
          Si vous n'attendiez pas cette invitation, vous pouvez ignorer cette page.
        </p>
      </Card>
    </div>
  );
}
